import { Close, UploadFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  athenaApi,
  useLazyGetApiLibraryByIdQuery,
  usePostApiAttachmentsMutation,
} from '../../../../app/athenaApiGen';
import {
  clearUploadPayload,
  setSelectedItem,
  toggleModal,
  updateUploadPayload,
} from '../../../../app/library/librarySlice';
import { RootState } from '../../../../app/store';
import UploadProgressDialog from '../../../../components/attachments/UploadProgressDialog';
import { useMultiFileUpload } from '../../../../components/attachments/useMultiFileUpload';
import { UsersAutocomplete } from './components/UsersAutocomplete';
import { UploaderModalContainer } from './StyledComponents';

// Guarantee the stored name keeps the picked file's extension. Mirrors the
// safety net previously living in Uploader.tsx so library labels never show
// "Report" instead of "Report.pdf" when a user trims the extension off.
const withExtension = (name: string, fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return name;
  const ext = fileName.slice(dot);
  return name.toLowerCase().endsWith(ext.toLowerCase())
    ? name
    : `${name}${ext}`;
};

const UploaderModal = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedItem, uploadPayload, modals } = useSelector(
    (state: RootState) => state.library,
  );
  const [uploadAttachment] = usePostApiAttachmentsMutation();
  const [fetchLibraryItem] = useLazyGetApiLibraryByIdQuery();
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const upload = useMultiFileUpload();
  const uploading = upload.state.phase === 'uploading';

  // The editable Name field only applies when a single file is picked. With
  // multiple files each attachment keeps its own filename, so we hide it.
  const singleFile = pickedFiles.length === 1;

  const handleClose = () => {
    dispatch(clearUploadPayload());
    dispatch(toggleModal('uploader'));
    setPickedFiles([]);
    setError('');
  };

  // Map frontend entityType → backend ObjectType (Project, Program, ...).
  const getObjectType = (): string => {
    if (!selectedItem?.entityType) return '';
    if (selectedItem.entityType.includes('directory')) return 'Directory';
    return (
      selectedItem.entityType.charAt(0).toUpperCase() +
      selectedItem.entityType.slice(1)
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files ?? []);
    if (incoming.length === 0) return;
    // Append to whatever's already picked so users can build a batch across
    // several "Add more files" clicks. De-dupe by name+size to avoid obvious
    // double-adds when the same file is selected twice.
    const next = [...pickedFiles];
    incoming.forEach((file) => {
      if (!next.some((f) => f.name === file.name && f.size === file.size)) {
        next.push(file);
      }
    });
    dispatch(
      updateUploadPayload({
        objectId: selectedItem?.id,
        objectType: getObjectType(),
        // Auto-populate the name from the first picked file (basename, no
        // path). Only meaningful for single-file uploads.
        name: next.length === 1 ? next[0].name : '',
      }),
    );
    setPickedFiles(next);
    setError('');
    // Reset the input so re-selecting the same file fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    const next = pickedFiles.filter((_, i) => i !== index);
    setPickedFiles(next);
    if (next.length === 1) {
      dispatch(updateUploadPayload({ name: next[0].name }));
    }
  };

  const handleUpload = async () => {
    if (pickedFiles.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }
    setError('');

    const objectId = uploadPayload.objectId || selectedItem?.id || '';
    const objectType = uploadPayload.objectType || getObjectType();
    const description = uploadPayload?.description || '';
    const creatorContactId = uploadPayload?.creatorContactId || '';

    // Loop the single-attachment endpoint once per file: the batch endpoint
    // can't carry a name or description, and these uploads share metadata.
    const tasks = pickedFiles.map((file) => ({
      file,
      upload: async (signal: AbortSignal) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('objectId', objectId);
        formData.append('objectType', objectType);
        // Single-file uploads honor the user-edited Name; multi-file
        // uploads keep each file's own name.
        formData.append(
          'name',
          singleFile
            ? withExtension(uploadPayload?.name ?? '', file.name)
            : file.name,
        );
        formData.append('description', description);
        formData.append('creatorContactId', creatorContactId);
        // @ts-ignore - body is FormData; the generated type expects an object.
        const request = uploadAttachment({ body: formData });
        signal.addEventListener('abort', () => request.abort(), {
          once: true,
        });
        return request.unwrap();
      },
    }));

    const result = await upload.start(tasks);
    const selectedId = selectedItem?.id;
    dispatch(athenaApi.util.invalidateTags(['Library']));

    if (result.errorCount > 0) {
      const failedNames = new Set(
        result.files
          .filter((f) => f.status === 'error')
          .map((f) => f.file.name),
      );
      setPickedFiles((prev) => prev.filter((f) => failedNames.has(f.name)));
    } else {
      dispatch(clearUploadPayload());
      dispatch(toggleModal('uploader'));
      setPickedFiles([]);
    }

    // Re-fetch the selected directory so its children include the new files
    // without requiring the user to re-click into it.
    if (selectedId) {
      try {
        const refreshed = await fetchLibraryItem({ id: selectedId }).unwrap();
        dispatch(setSelectedItem(refreshed));
      } catch {
        /* tag-invalidation above will resolve on next interaction */
      }
    }
  };

  // Build a contextual phrase like "the BOSS project" / "the Library folder"
  // so the subtitle reads naturally instead of dropping a bare name on the
  // user. Falls back to the plain label when the entity type is unknown.
  const entityWord = (() => {
    switch (selectedItem?.entityType) {
      case 'program':
        return 'program';
      case 'project':
        return 'project';
      case 'library_directory':
        return 'folder';
      case 'deliverable':
        return 'deliverable';
      default:
        return '';
    }
  })();
  const targetLabel = selectedItem?.label ?? 'this folder';
  // Name is only required for single-file uploads (multi-file uses each
  // file's own name). Owner is always required.
  const canSubmit =
    pickedFiles.length > 0 &&
    !!uploadPayload?.creatorContactId &&
    (!singleFile || !!uploadPayload?.name);

  return (
    <>
    <Modal
      open={modals.uploader.open}
      onClose={handleClose}
      aria-labelledby="uploader-modal-title"
    >
      <UploaderModalContainer>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography id="uploader-modal-title" variant="h6" fontWeight={600}>
              Add Files
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {entityWord ? (
                <>
                  Upload files to the <strong>{targetLabel}</strong>{' '}
                  {entityWord}
                </>
              ) : (
                <>
                  Upload files to <strong>{targetLabel}</strong>
                </>
              )}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close"
            disabled={uploading}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {/* Body */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'auto',
          }}
        >
          {/* Name only applies to single-file uploads; multiple files each
              keep their own filename, so the field is hidden then. */}
          {pickedFiles.length <= 1 && (
            <TextField
              required
              autoFocus
              size="small"
              label="Name"
              variant="outlined"
              value={uploadPayload?.name ?? ''}
              onChange={(e) =>
                dispatch(updateUploadPayload({ name: e.target.value }))
              }
              disabled={uploading}
            />
          )}
          <TextField
            size="small"
            label="Description"
            variant="outlined"
            multiline
            minRows={2}
            value={uploadPayload?.description ?? ''}
            onChange={(e) =>
              dispatch(updateUploadPayload({ description: e.target.value }))
            }
            disabled={uploading}
          />
          <UsersAutocomplete
            onChange={(newContactId) => {
              dispatch(
                updateUploadPayload({
                  creatorContactId: newContactId ?? null,
                }),
              );
            }}
          />

          {/* File picker last — Name/Description/Owner come first so the user
              fills out metadata and ends on the action that completes the
              form. The hidden input is triggered via the Button's
              component="label". */}
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            disabled={uploading}
            sx={(theme) => ({
              borderStyle: 'dashed',
              borderColor: theme.palette.divider,
              color: 'text.primary',
              py: 2,
              justifyContent: 'flex-start',
              textTransform: 'none',
              '&:hover': {
                borderStyle: 'dashed',
                borderColor: theme.palette.primary.main,
              },
            })}
          >
            {pickedFiles.length > 0
              ? 'Add more files…'
              : 'Choose files to upload'}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              onChange={handleFileChange}
              disabled={uploading}
            />
          </Button>
          {pickedFiles.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {pickedFiles.length === 1
                  ? 'Selected:'
                  : `Selected (${pickedFiles.length}):`}
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {pickedFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}-${file.size}-${index}`}
                    size="small"
                    label={file.name}
                    onDelete={uploading ? undefined : () => removeFile(index)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </Box>
        <Divider />

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button onClick={handleClose} disabled={uploading} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!canSubmit || uploading}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </Box>
      </UploaderModalContainer>
    </Modal>
    <UploadProgressDialog
      open={upload.state.phase !== 'idle'}
      state={upload.state}
      onCancel={upload.cancel}
      onClose={upload.reset}
    />
    </>
  );
};

export default UploaderModal;
